import { ArrowsClockwise, CheckCircle, Link, Sliders } from "@phosphor-icons/react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import StatCard from "../../../../components/StatCard";
import { useAuth } from "../../../../hooks";
import { useIntegrationApps } from "../../../../hooks/get/useIntegrationApps";
import { isToday, parseISO, format, isYesterday, formatDistanceToNow } from "date-fns";
import { de, fr, es, ja } from "date-fns/locale";

interface IntegrationStatsProps {
  activePairs: number;
  activeOnly: number;
  ongoingPairs: number;
  syncHealth: number;
  lastPull: string;
}

const IntegrationStats: React.FC<IntegrationStatsProps> = ({
  activePairs,
  activeOnly,
  ongoingPairs,
  syncHealth,
  lastPull,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const dateLocale = useMemo(() => {
    switch (i18n.language) {
      case "de": return de;
      case "fr": return fr;
      case "es": return es;
      case "ja": return ja;
      default: return undefined;
    }
  }, [i18n.language]);
  const { isVip, subscriptionDetails } = useAuth();

  const maxApps = subscriptionDetails?.overrides?.max_apps ?? subscriptionDetails?.plan_limits?.max_apps ?? 0;

  const { data: appsData } = useIntegrationApps();
  const allApps = appsData?.apps ?? [];
  const selectedAppsCount = allApps.filter((a) => a.is_sync_on).length;
  const hasNoAppsSelected = !isVip && allApps.length > 0 && allApps.every((a) => !a.is_sync_on);

  const lastSyncedTooltipText = useMemo(() => {
    const lastSynced = lastPull;

    if (!lastSynced) return "";

    const lastSyncedDate = parseISO(lastSynced);

    if (isToday(lastSyncedDate)) {
      return t("common.lastSynced.today", {
        time: format(lastSyncedDate, "h:mm a"),
      });
    } else if (isYesterday(lastSyncedDate)) {
      return t("common.lastSynced.yesterday", {
        time: format(lastSyncedDate, "h:mm a"),
      });
    } else {
      return t("common.lastSynced.date", {
        date: format(lastSyncedDate, "MMM d"),
        time: format(lastSyncedDate, "h:mm a"),
      });
    }
  }, [lastPull, t]);

  const lastPullValue = useMemo(() => {
    if (!lastPull) return "-";
    try {
      return formatDistanceToNow(new Date(lastPull), {
        addSuffix: true,
        locale: dateLocale,
      });
    } catch (e) {
      console.error("Error formatting sync time:", e);
      return "N/A";
    }
  }, [lastPull, dateLocale]);


  return (
    <div className={`grid grid-cols-1 gap-4 mb-6 ${isVip || activePairs === 0 ? "md:grid-cols-3" : "md:grid-cols-4"}`}>
      <StatCard
        title={t("configurations.integrations.stats.connectedPairs")}
        value={activePairs}
        icon={<Link size={24} weight="duotone" />}
        color="indigo"
        subStats={[
          { label: t("configurations.integrations.stats.active"), value: activeOnly },
          { label: t("configurations.integrations.stats.ongoing"), value: ongoingPairs },
        ]}
      />
      <StatCard
        title={t("configurations.integrations.stats.systemHealth")}
        value={t("configurations.integrations.stats.syncPercentage", { percentage: syncHealth })}
        icon={<ArrowsClockwise size={24} weight="duotone" />}
        color="indigo"
      />
      <StatCard
        title={t("configurations.integrations.stats.lastDataPull")}
        value={lastPullValue}
        icon={<CheckCircle size={24} weight="duotone" />}
        color="indigo"
        tooltip={true}
        tooltipContent={lastSyncedTooltipText}
        tooltipDirection="right"
      />
      {!isVip && activePairs > 0 && (
        <>
          <style>{`
            @keyframes card-border-ripple {
              0%   { box-shadow: 0 0 0 0px rgba(38,168,95,0.6); transform: scale(1); }
              70%  { box-shadow: 0 0 0 15px rgba(38,168,95,0); transform: scale(1.02); }
              100% { box-shadow: 0 0 0 0px rgba(38,168,95,0); transform: scale(1); }
            }
          `}</style>
          <button
            onClick={() => navigate("/app-selection")}
            className="relative flex flex-col justify-between rounded-xl overflow-hidden shadow-sm border border-indigo-300 bg-gradient-to-br from-indigo-600 via-secondary-900 to-secondary-600 text-white cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all duration-200 group px-4 pt-4 pb-0 w-full"
            style={hasNoAppsSelected ? { animation: "card-border-ripple 2s ease-out infinite" } : undefined}
          >
            {/* Subtle background glow */}
            <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />

            {/* Header row */}
            <div className="flex items-start justify-between w-full">
              <div className="text-left">
                <p className="text-xs font-semibold text-indigo-200 uppercase tracking-widest">
                  {t("configurations.integrations.stats.appSelectionLabel")}
                </p>
                <p className="text-2xl font-bold text-white mt-1.5 leading-none">
                  {t("configurations.integrations.stats.manageApps")}
                </p>
              </div>
              <div className="flex-shrink-0 ml-3 p-2.5 rounded-xl bg-white/20 text-white">
                <Sliders size={24} weight="duotone" />
              </div>
            </div>

            {/* Footer — app selection count */}
            <div className="flex items-center justify-between mt-3 pb-4">
              <div className="flex items-center gap-1.5 text-indigo-100 text-xs font-medium">
                <span className="text-white font-bold">{selectedAppsCount}</span>
                <span className="text-indigo-200">{t("configurations.integrations.stats.ofMaxAppsSyncing", { max: maxApps })}</span>
              </div>
              <span className="text-indigo-300 text-xs">→</span>
            </div>
          </button>
        </>
      )}
    </div>
  );
};

export default IntegrationStats;
